import React from 'react';
import { ChevronRight, MapPin, Star } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

const Hero = ({ data, onContactClick }) => {
  const { t } = useTranslation();
  
  // Use data from props if available, otherwise fall back to i18n
  const badge = data?.badge || t('hero.badge');
  const title = data?.title || t('hero.title');
  const subtitle = data?.subtitle || t('hero.subtitle');
  const description = data?.description || t('hero.description');
  const stats = data?.stats || [
    { value: "42", label: t('hero.stats.rooms') },
    { value: "4.390 m²", label: t('hero.stats.land') },
    { value: "~60", label: t('hero.stats.units') }
  ];
  
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Image with Enhanced Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src={data.backgroundImage} 
          alt={title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/75"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center">
        <div className="max-w-6xl mx-auto">
          {/* Premium Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/30 rounded-full px-6 py-3 mb-8">
            <Star className="w-5 h-5 text-[#C9A961]" fill="#C9A961" />
            <span className="text-white font-semibold tracking-wide text-sm">{badge}</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight">
            {title}
          </h1>
          <p className="text-2xl md:text-3xl text-white/90 mb-4 font-light">
            {subtitle}
          </p>
          <p className="text-lg md:text-xl text-white/80 max-w-4xl mx-auto mb-8 leading-relaxed">
            {description}
          </p>

          {/* Location Badge */}
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/30 rounded-full px-6 py-3">
              <MapPin className="w-5 h-5 text-[#C9A961]" />
              <span className="text-white text-sm font-medium">{t('hero.location')}</span>
            </div>
          </div>

          {/* CTA Button */}
          <div className="mb-8">
            <Button 
              onClick={onContactClick}
              size="lg"
              className="bg-gradient-to-r from-[#C9A961] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#E5C158] text-white px-12 py-7 text-xl font-bold rounded-xl shadow-2xl hover:shadow-[#C9A961]/50 transition-all duration-300 hover:scale-105"
            >
              {t('hero.cta')}
              <ChevronRight className="ml-2 w-6 h-6" />
            </Button>
          </div>

          {/* Stats Cards - Dynamic from data */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-md border border-white/20 rounded-xl p-8">
                <div className="text-5xl font-bold text-[#C9A961] mb-3">{stat.value}</div>
                <div className="text-white/90 font-medium uppercase tracking-wider text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </section>
    );
  };
  
  export default Hero;